import { useEffect, useMemo, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { Download, MessageCircle, Zap } from "lucide-react";
import { rupiah } from "@/lib/api";
import { storeLink } from "./StoreButtons";
import { Reveal, SectionLabel } from "./Reveal";

const PRESETS = [50000, 100000, 250000, 500000, 1000000];
const DESTS = ["DANA", "OVO", "GoPay", "ShopeePay", "LinkAja", "BCA", "BRI", "BNI", "Mandiri"];

const AnimatedRupiah = ({ value }) => {
  const mv = useSpring(value, { stiffness: 90, damping: 20 });
  const text = useTransform(mv, (v) => rupiah(v));
  useEffect(() => {
    mv.set(value);
  }, [value, mv]);
  return <motion.span data-testid="calc-result-amount">{text}</motion.span>;
};

const Chip = ({ active, onClick, children, testId, pad = "px-4 py-2" }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`rounded-full border ${pad} text-sm transition-colors duration-300 ${active ? "border-volt bg-volt text-ink" : "border-white/15 text-zinc-300 hover:border-white/40 hover:text-paper"}`}
  >
    {children}
  </button>
);

export const Calculator = ({ rates, settings }) => {
  const [opKey, setOpKey] = useState(null);
  const [amount, setAmount] = useState(100000);
  const [dest, setDest] = useState("DANA");
  const op = useMemo(() => rates.find((r) => r.key === opKey) || rates[0], [rates, opKey]);
  const received = Math.floor((amount * (op?.rate ?? 0)) / 100);
  const appHref = storeLink(settings, "playstore") || storeLink(settings, "appstore");
  const wa = settings.whatsapp
    ? `https://wa.me/${settings.whatsapp}?text=${encodeURIComponent(`Halo BagiPulsa, saya mau convert pulsa ${op?.name} ${rupiah(amount)} ke ${dest}.`)}`
    : null;

  return (
    <section id="kalkulator" data-testid="calculator-section" className="relative mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-36">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <SectionLabel index="01">Kalkulator</SectionLabel>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[1] tracking-tight text-paper md:text-5xl">Hitung dulu, <span className="text-volt">baru tukar.</span></h2>
          <p className="mt-6 max-w-sm text-zinc-300">Pilih operator, masukkan nominal pulsa, dan lihat langsung berapa uang yang kamu terima. Rate mengikuti update harian kami.</p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-8">
          <div className="cut-corner relative overflow-hidden bg-surface p-6 ring-1 ring-white/[0.07] md:p-10">
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-volt/10 blur-3xl" />
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Operator</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {rates.map((r) => (
                <Chip key={r.key} pad="py-1.5 pl-1.5 pr-4" testId={`calc-operator-${r.key}`} active={op?.key === r.key} onClick={() => setOpKey(r.key)}>
                  <span className="flex items-center gap-2">
                    <span className="grid h-6 w-6 shrink-0 place-items-center overflow-hidden rounded-full bg-white">
                      <img src={`/img/ops/${r.key}.webp`} alt="" className="h-5 w-5 object-contain" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                    </span>
                    {r.name} · {r.rate}%
                  </span>
                </Chip>
              ))}
            </div>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Nominal pulsa</p>
            <div className="mt-3 flex items-baseline gap-2 border-b border-white/15 pb-3 focus-within:border-volt">
              <span className="font-display text-2xl text-zinc-400 md:text-4xl">Rp</span>
              <input
                data-testid="calc-nominal-input"
                inputMode="numeric"
                value={amount ? new Intl.NumberFormat("id-ID").format(amount) : ""}
                onChange={(e) => setAmount(Math.min(Number(e.target.value.replace(/\D/g, "")) || 0, 100000000))}
                placeholder="0"
                className="w-full bg-transparent font-display text-3xl font-semibold text-paper outline-none placeholder:text-zinc-600 md:text-5xl"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <Chip key={p} testId={`calc-preset-${p}`} active={amount === p} onClick={() => setAmount(p)}>
                  {p >= 1000000 ? "1 jt" : `${p / 1000} rb`}
                </Chip>
              ))}
            </div>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Cair ke</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {DESTS.map((d) => (
                <Chip key={d} testId={`calc-dest-${d.toLowerCase()}`} active={dest === d} onClick={() => setDest(d)}>{d}</Chip>
              ))}
            </div>
            <div className="mt-10 flex flex-col gap-6 rounded-2xl bg-ink/70 p-6 ring-1 ring-volt/20 md:flex-row md:items-end md:justify-between md:p-8">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Kamu terima di {dest}</p>
                <p className="mt-2 font-display text-4xl font-bold tracking-tight text-volt md:text-6xl"><AnimatedRupiah value={received} /></p>
                <p className="mt-3 flex items-center gap-2 text-sm text-zinc-300" data-testid="calc-summary">
                  <Zap className="h-4 w-4 text-lime" /> Rate {op?.rate}% · Biaya admin Rp0 · Proses 1–10 menit
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row md:flex-col">
                {appHref ? (
                <a data-testid="calc-download-app" href={appHref} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-volt px-6 py-3.5 font-semibold text-ink transition-transform duration-300 hover:scale-[1.04]">
                  <Download className="h-4 w-4" /> Convert via App
                </a>
                ) : (
                <span data-testid="calc-download-app-soon" className="flex cursor-not-allowed items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3.5 font-semibold text-zinc-400">Aplikasi segera hadir</span>
                )}
                {wa ? (
                  <a data-testid="calc-whatsapp" href={wa} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-3.5 font-semibold text-paper transition-colors hover:border-lime hover:text-lime">
                    <MessageCircle className="h-4 w-4" /> Tanya CS
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
